<script setup>
import BusinessHealthPanel from '@/components/business-health/BusinessHealthPanel.vue'
import CoachingInsightsPanel from '@/components/insights/CoachingInsightsPanel.vue'
import FinancialInsightPanel from '@/components/financial-insight/FinancialInsightPanel.vue'
import { DEFAULT_PERIOD, PERIOD_KEYS, periodLabelKey } from '@/stores/insightPeriods'

definePage({
  meta: {
    navActiveLink: 'insights',
  },
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const LENS_VALUES = new Set(['coaching', 'business', 'financial'])

const lensFromQuery = queryLens => {
  if (queryLens === 'business' || queryLens === 'financial')
    return queryLens

  return 'coaching'
}

const activeLens = ref(lensFromQuery(route.query.lens))
const periodKey = ref(DEFAULT_PERIOD)

const periodItems = computed(() =>
  PERIOD_KEYS.map(key => ({
    title: t(periodLabelKey(key)),
    value: key,
  })),
)

const showPeriodSelect = computed(() =>
  activeLens.value === 'business' || activeLens.value === 'financial',
)

watch(activeLens, lens => {
  const query = { ...route.query }
  if (lens === 'coaching')
    delete query.lens
  else
    query.lens = lens

  router.replace({ query })
})

watch(() => route.query.lens, lens => {
  const next = lensFromQuery(lens)
  if (LENS_VALUES.has(next))
    activeLens.value = next
})

const switchToCoaching = () => {
  activeLens.value = 'coaching'
}
</script>

<template>
  <div>
    <div class="d-flex flex-wrap align-center justify-space-between gap-4 mb-6">
      <div>
        <h1 class="text-h4 mb-1">
          {{ $t('insights.pageTitle') }}
        </h1>
        <p class="text-medium-emphasis mb-0">
          {{ $t('insights.pageSubtitle') }}
        </p>
      </div>
      <VSelect
        v-if="showPeriodSelect"
        v-model="periodKey"
        :items="periodItems"
        item-title="title"
        item-value="value"
        :label="$t('businessHealth.periodLabel')"
        density="compact"
        style="max-inline-size: 14rem;"
        hide-details
      />
    </div>

    <VTabs
      v-model="activeLens"
      class="v-tabs-pill mb-4"
      :aria-label="$t('insights.lensTabsAria')"
    >
      <VTab value="coaching">
        {{ $t('insights.lensCoaching') }}
      </VTab>
      <VTab value="business">
        {{ $t('insights.lensBusiness') }}
      </VTab>
      <VTab value="financial">
        {{ $t('insights.lensFinancial') }}
      </VTab>
    </VTabs>

    <VWindow
      v-model="activeLens"
      class="disable-tab-transition"
      :touch="false"
    >
      <VWindowItem
        value="coaching"
        eager
      >
        <CoachingInsightsPanel />
      </VWindowItem>
      <VWindowItem
        value="business"
        eager
      >
        <BusinessHealthPanel
          :period-key="periodKey"
          @open-coaching-insights="switchToCoaching"
        />
      </VWindowItem>
      <VWindowItem
        value="financial"
        eager
      >
        <FinancialInsightPanel :period-key="periodKey" />
      </VWindowItem>
    </VWindow>
  </div>
</template>
